/**
 * Splits the truck settlement "Adjustments and advances" bucket (TrucksPayrollSummary.LoanBalances)
 * into Fuel, Tolls and the remaining adjustments and advances.
 *
 * Mirrors accounting/driver-payroll/dialogs/payroll-batch-process-dialog.jsx, which itself mirrors
 * the settlement PDF (DriverPayrollUseCases::buildSettlementCategories). If that backend rule
 * changes, the dialog and this module need the same change.
 *
 * Pure and import-free on purpose: importing anything that reaches src/util/util-formaters
 * (Field, FieldsManager, util-helpers) breaks vitest.
 */

// Maps an open-pay-entry row (openPayEntryData[].Item) to its bucket in splitLoanBalances' result.
// The three rows drill into the same modal, each showing only its own bucket.
export const LOAN_BALANCES_CATEGORY = {
    FuelTotal: 'fuel',
    TollsTotal: 'tolls',
    LoanBalancesTotal: 'other'
};

// Case-sensitive, and the trailing space is required - same as the backend's str_starts_with.
export function isTollItem(item) {
    return (item?.Description ?? '').startsWith('Toll ');
}

/**
 * Case-insensitive, and the trailing space is required, so prefix "FUEL" matches
 * "FUEL 07.01. Dallas, TX" but not "FUELCARD 07.04. Reno, NV". An empty prefix
 * (tbl_AccountingSettings.FuelImportPrefix not set) disables the fuel split.
 */
export function isFuelItem(item, fuelImportPrefix) {
    const prefix = (fuelImportPrefix ?? '').trim();

    return prefix !== ''
        && (item?.Description ?? '').toLowerCase().startsWith(prefix.toLowerCase() + ' ');
}

/**
 * Classification is by memo only - LoanBalances rows mix tbl_ExpenseLine rows with open
 * reimbursements and are only guaranteed to carry Description/Amount. Tolls are matched first and
 * win over fuel, matching the backend, which pulls tolls out before the fuel pass.
 */
export function splitLoanBalances(items, fuelImportPrefix) {
    const rows = Object.values(items ?? {});

    return {
        tolls: rows.filter(isTollItem),
        fuel: rows.filter(it => !isTollItem(it) && isFuelItem(it, fuelImportPrefix)),
        other: rows.filter(it => !isTollItem(it) && !isFuelItem(it, fuelImportPrefix))
    };
}

// LoanBalances item amounts come in with the opposite sign of the total the summary shows
// (PayrollManager::readTruckSummary negates the summed amounts), so category totals negate too.
// Subtracting from 0 rather than multiplying by -1 keeps an empty bucket at 0 instead of -0, which
// the money formatter would otherwise render as "-$0.00".
export function sumLoanBalancesAmount(items) {
    return 0 - (items ?? []).reduce((memo, it) => memo + (parseFloat(it?.Amount) || 0), 0);
}

/**
 * The remainder is taken off the API total instead of summed from the `other` bucket, so the three
 * rows still add up to the LoanBalancesTotal the net position is calculated from.
 */
export function getLoanBalancesTotals(split, loanBalancesTotal) {
    const fuelTotal = sumLoanBalancesAmount(split?.fuel);
    const tollsTotal = sumLoanBalancesAmount(split?.tolls);

    return {
        fuelTotal: fuelTotal,
        tollsTotal: tollsTotal,
        adjustmentsAndAdvancesTotal: (loanBalancesTotal ?? 0) - fuelTotal - tollsTotal
    };
}
